/**
 * Ponto e Jornada — falta e exposição a trabalho noturno do funcionário, calculadas a partir
 * das marcações de ponto sincronizadas de uma integração (Ponto Icarus, hoje).
 */

import React, { useCallback } from 'react';
import { Card, CardContent, Box, Typography, Chip, Skeleton, Alert, Stack } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import { attendanceRiskService, AttendanceRisk } from '../../../services/attendanceRiskService';

interface EmployeeCardProps {
  employeeId: number;
  companyId: number;
}

const ACCENT = '#00695C';

function formatDate(d: string): string {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR');
  } catch {
    return d;
  }
}

export const EmployeeAttendanceRiskCard: React.FC<EmployeeCardProps> = ({ employeeId }) => {
  const carregar = useCallback(
    () => attendanceRiskService.getForEmployee(employeeId, 30),
    [employeeId]
  );
  const dado = useDadoPermitido<AttendanceRisk>('employee.read', 'Ponto e Jornada', carregar);
  const loading = dado.carregando;
  const risco = dado.dado;

  const faltas = risco?.faltas.filter(f => f.falta) ?? [];
  const exposicaoNoturna = risco?.exposicaoNoturna.filter(e => e.exposicaoNoturna) ?? [];

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><AccessTime fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Ponto e Jornada
          </Typography>
          {!loading && !dado.erro && risco && (
            <Chip label={faltas.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {loading && (
          <Stack spacing={1}>
            {[0, 1].map(i => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {dado.erro && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{dado.erro}</Alert>
        )}

        {!loading && !dado.erro && !risco && (
          <Typography variant="body2" color="text.secondary">
            {dado.bloqueio ?? 'Nenhuma integração de ponto configurada para esta empresa'}
          </Typography>
        )}

        {!loading && !dado.erro && risco && (
          <>
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
              <Chip
                label={`${faltas.length} falta${faltas.length !== 1 ? 's' : ''} (30 dias)`}
                size="small"
                variant="outlined"
                color={faltas.length > 0 ? 'warning' : 'default'}
              />
              <Chip
                label={`${exposicaoNoturna.length} dia${exposicaoNoturna.length !== 1 ? 's' : ''} com trabalho noturno`}
                size="small"
                variant="outlined"
              />
            </Box>

            {faltas.length > 0 && (
              <Stack spacing={0.75}>
                {faltas.slice(0, 5).map(f => (
                  <Box
                    key={f.data}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                  >
                    <Typography variant="caption">{formatDate(f.data)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      Sem marcação de ponto
                    </Typography>
                  </Box>
                ))}
                {faltas.length > 5 && (
                  <Typography variant="caption" color="text.secondary">
                    + {faltas.length - 5} outra(s)
                  </Typography>
                )}
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeAttendanceRiskCard;
